import React, { useCallback, useContext, useEffect, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react-lite";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import styles from "./AssemblyVoting.module.css";

interface IAssemblyVotingBase {
    assemblyid?: string;
}

const AssemblyVotingBase = observer((props: IAssemblyVotingBase) => {
    const { votingStore } = useContext(RootStoreContext);
    const { t } = useTranslation();

    useEffect(() => {
        if (votingStore.selectedVoting.votingid) {
            votingStore.setVotingComponentResponse([]);
            votingStore.loadVotingTenantsAndOptions();
        }
    }, [votingStore.selectedVoting.votingid, votingStore]);

    // after loadVotingTenantsAndOptions() runs and votingResults/votingTenants/votingOptions are fresh:
    useEffect(() => {
        const { votingTenants, votingOptions, votingResults } = votingStore;

        if (
            votingStore.selectedVoting.votingid &&
            votingTenants.length > 0 &&
            votingOptions.length > 0 &&
            votingResults.length > 0
        ) {
            const initialResponse = votingTenants.map((tenant) => {
                const result = votingResults.find((r) => r.tenantid === tenant.tenantid);
                if (!result?.votingoptionid) {
                    return { id: tenant.tenantid, selectedColumn: null };
                }
                const optIndex = votingOptions.findIndex((o) => o.votingoptionid === result.votingoptionid);
                return { id: tenant.tenantid, selectedColumn: optIndex >= 0 ? optIndex + 1 : null };
            });

            votingStore.setVotingComponentResponse(initialResponse);
        }
    }, [
        votingStore,
        votingStore.selectedVoting.votingid,
        votingStore.votingTenants,
        votingStore.votingOptions,
        votingStore.votingResults,
        votingStore.setVotingComponentResponse
    ]);

    const pieChartList = useMemo(() => {
        const counts = new Array(votingStore.votingOptions.length).fill(0);

        votingStore.votingComponentResponse.forEach((response) => {
            if (response.selectedColumn !== null && response.selectedColumn >= 1) {
                const index = response.selectedColumn - 1;
                if (index < counts.length) {
                    counts[index]++;
                }
            }
        });

        return votingStore.votingOptions.map((option, index) => ({
            label: option.text,
            color: votingStore.votingColors?.[index] || "#ccc",
            number: counts[index] || 0
        }));
    }, [votingStore.votingComponentResponse, votingStore.votingOptions, votingStore.votingColors]);

    const tenantRows = useMemo(() => {
        return votingStore.votingTenants.map((tenant) => {
            const resp = votingStore.votingComponentResponse.find((r) => r.id === tenant.tenantid);

            return {
                id: tenant.tenantid,
                label: tenant.name,
                selectedRow: resp?.selectedColumn ?? 0
            };
        });
    }, [votingStore.votingTenants, votingStore.votingComponentResponse]);

    const handleSelectionChange = useCallback(
        (data: { id: string; selectedColumn: number | null }[]) => {
            votingStore.setVotingComponentResponse(data);
        },
        [votingStore]
    );

    const handleSave = useCallback(() => {
        votingStore.saveVotingResult();
    }, [votingStore]);

    if (
        votingStore.votingList.length === 0 ||
        !votingStore.selectedVoting.votingid ||
        votingStore.votingTenants.length === 0 ||
        votingStore.votingOptions.length === 0 ||
        votingStore.votingResults.length === 0
    ) {
        return null;
    }

    const initialDataVersion = votingStore.votingComponentResponse.map((r) => r.selectedColumn ?? 0).join(",");

    return (
        <UI.Card>
            <div className={styles.VotingTitle}>{votingStore.selectedVoting.question}</div>
            {pieChartList.some((item) => item.number && item.number > 0) && (
                <div className={styles.PieChart}>
                    <UI.PieChart pieChartList={pieChartList} />
                </div>
            )}

            <UI.Voting
                key={`${votingStore.selectedVoting.votingid}-${initialDataVersion}`}
                numberOfColumn={votingStore.votingOptions.length}
                headers={["WQ", ...votingStore.votingOptions.map((o) => o.text)]}
                withMainRow={true}
                rows={tenantRows}
                secondRowLabels={votingStore.votingTenants.map((tenant) => tenant.unitvalue.toString())}
                buttonColors={votingStore.votingColors}
                onSelectionChange={handleSelectionChange}
                withSearchInput={true}
            />
            <div className={styles.SaveButtonWrapper}>
                <UI.Button
                    onClick={handleSave}
                    className={styles.SaveButton}
                    label={t("labels.save")}
                    disabled={!votingStore.hasVotingResultChanged}
                />
            </div>
        </UI.Card>
    );
});

export const AssemblyVoting = Sentry.withProfiler(AssemblyVotingBase);
